import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import AppKitProvider from "./providers/AppKitProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { Toaster } from "sonner";
import { HelmetProvider } from 'react-helmet-async';
import { validateEnv } from "./utils/validateEnv";
import { initCSPHealthCheck } from "./utils/csp-health-check";
import { fixMobileWalletConnections } from "./utils/secureWeb3Provider";
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

// Silence non-error console output in production
if (import.meta.env.PROD) {
  const noop = () => {};
  // Keep console.error for critical issues
  console.log = noop;
  console.info = noop;
  console.debug = noop;
  console.warn = noop;
}

// Detect if we're on a mobile device
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Validate environment variables before app starts
if (!validateEnv()) {
  throw new Error("Application cannot start due to missing or invalid environment variables");
}

// Initialize CSP diagnostics in development mode only
if (import.meta.env.DEV) {
  initCSPHealthCheck();
}

// Initialize Capacitor Status Bar for mobile apps
const initializeNativeFeatures = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Configure status bar
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#000000' });
      await StatusBar.setOverlaysWebView({ overlay: false });
      console.log('Status bar initialized successfully');
    } catch (error) {
      console.warn('Status bar initialization failed:', error);
    }
  }
};

// Initialize mobile wallet fixes if on a mobile device
if (isMobile) {
  // We'll initialize this after the DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixMobileWalletConnections);
  } else {
    fixMobileWalletConnections();
  }
  
  console.log('Mobile device detected, applying wallet connection optimizations');
}

// Initialize native features for Capacitor apps
initializeNativeFeatures();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Failed to find the root element");
}

const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <HelmetProvider>
      <AppKitProvider>
        <ThemeProvider>
          <App />
          <Toaster richColors position="top-right" />
        </ThemeProvider>
      </AppKitProvider>
    </HelmetProvider>
  </StrictMode>
);